"use client";

import { globalStyleObj } from "@/app/assets/styles";
import {
  CommonJoditReactTextEditor,
  CommonTextAreaField,
  CommonTextInputField,
  FileReuseDialog,
  InvestmentCommonFields,
  LabelText,
  LanguageTabSwitcher,
  PageRefresh,
  RepeatableTextTextTextInput,
  RepeatableThreeInputsWithImage,
  RepeatableTwoInputsWithTextEditor,
  RepeatableTwoTextInputFields,
  SubmitButton,
} from "@/components";
import { useErrorToast, useGenerateSlug, useSuccessToast } from "@/lib/hooks";
import { InvestmentValidationSchema } from "@/schemas/pagesSchema/investmentValidationSchema";
import { useAppSelector } from "@/store/hooks";
import { getCustomTheme } from "@/utils/colors";
import { usePageRefresh } from "@/utils/refreshPage";
import { getFileSettingsValue } from "@/utils/website-settings-helper";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTheme } from "next-themes";
import { useSearchParams } from "next/navigation";
import React, { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { MdBusiness } from "react-icons/md";

const InvestmentForm = ({
  formType = "CREATE_FORM",
  userId,
  languages = [],
  activeLanguageTab = "",
  targetId = "",
  filesList,
  allFiles,
  paginationDetails,
  itemDetails = {},
  createFnc = () => {},
  updateFnc = () => {},
  searchValue,
  selectedFileType,
  adminRole,
  permissionsList,
}) => {
  const searchParams = useSearchParams();
  const activeSection = searchParams.get("tab");

  const { isPending, pageRefresh } = usePageRefresh();
  const { layoutThemePrimaryColorType } = useAppSelector(
    (state) => state.layout
  );
  const { theme } = useTheme();
  const { bgColor, hoverBgColor, textColor, hexCode } = useMemo(
    () => getCustomTheme({ layoutThemePrimaryColorType }),
    [layoutThemePrimaryColorType]
  );
  const isItemDetailsExist = Object.keys(itemDetails).length > 0;
  const isHideInOtherLanguages = useMemo(() => {
    return formType === "CREATE_FORM" ? true : activeLanguageTab === "en";
  }, [activeLanguageTab]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
    setError,
    setValue,
  } = useForm({
    resolver: zodResolver(InvestmentValidationSchema),
    defaultValues: itemDetails,
  });
  // Watch Form Data
  const formData = watch();
  const watchedName = watch("name") || "";

  // Reset the form state when itemDetails changes
  useEffect(() => {
    if (Object.keys(itemDetails).length > 0 && formType === "UPDATE_FORM") {
      reset(itemDetails);
    }
  }, [itemDetails, reset]);

  // Handle Auto Generated slug value based on the name
  useEffect(() => {
    const generateSlugWithTitle = () => {
      if (watchedName && watchedName.length > 0) {
        setError("slug", "");
        const generatedSlug = useGenerateSlug(watchedName);
        setValue("slug", generatedSlug);
      } else {
        setValue("slug", "");
      }
    };

    if (formType === "CREATE_FORM") {
      generateSlugWithTitle();
    }
  }, [watchedName]);

  // Handle Image Change
  const onChangeImageFiled = useCallback(
    (keyname, id) => {
      if (keyname) {
        setValue(keyname, id);
      }
    },
    [setValue]
  );

  // Handle On Change Text Editor Functionality
  const handleOnChangeTextEditor = useCallback(
    (value, keyName) => {
      if (value) {
        setValue(keyName, value);
      } else {
        setValue(keyName, "");
      }
    },
    [setValue]
  );

  // Handle Add Repeatable Field Functionality
  const handleAddRepeatableField = useCallback(
    (keyName, labelNames = []) => {
      const watchedField = watch(keyName) || {}; // Get existing values or an empty object

      // Loop through labelNameList and add values dynamically
      const updatedField = labelNames.reduce((acc, key) => {
        acc[key] = [...(watchedField[key] || []), ""];

        return acc;
      }, {});

      setValue(keyName, {
        ...watchedField,
        ...updatedField,
      });
    },
    [setValue, watch]
  );

  // Handle On Change Repeatable Fields
  const handleOnChangeRepeatableFields = useCallback(
    (keyName, labelName, value, index) => {
      const watchedField = watch(keyName) || {}; // Get existing data

      // Ensure the field exists and is an array
      const updatedField = {
        ...watchedField,
        [labelName]: watchedField[labelName].map((item, i) =>
          i === index ? value : item
        ),
      };

      setValue(keyName, updatedField);
    },
    [setValue, watch]
  );

  // Handle Remove Repeatable Field Functionality
  const handleRemoveRepeatableField = useCallback(
    (keyName, labelNames = [], index) => {
      const watchedField = watch(keyName) || {}; // Get existing values or an empty object

      // Loop through labelNameList and remove the item at the given index
      const updatedField = labelNames.reduce((acc, key) => {
        acc[key] = watchedField[key]?.filter((_, i) => i !== index) || [];

        return acc;
      }, {});

      setValue(keyName, {
        ...watchedField,
        ...updatedField,
      });
    },
    [setValue, watch]
  );

  // Handle Form Validation Errors
  const handleValidationErrors = useCallback(
    (errors) => {
      Object.keys(errors).forEach((field) => {
        setError(field, {
          type: "server",
          message: errors[field].message,
        });
      });
    },
    [setError]
  );

  // Handle Form Submit Functionality
  const onSubmit = async (data) => {
    if (formType === "CREATE_FORM") {
      const response = await createFnc(formData, userId);

      if (response.success) {
        useSuccessToast(
          response?.message || "Investment created successfully."
        );
        reset();
      } else {
        useErrorToast(response?.message || "Something went wrong");
      }
    }

    if (formType === "UPDATE_FORM") {
      const response = await updateFnc(
        userId,
        targetId,
        formData,
        activeLanguageTab
      );

      if (response.success) {
        useSuccessToast(
          response?.message || "Investment updated successfully."
        );
        pageRefresh();
      } else {
        useErrorToast(response?.message || "Something went wrong");
      }
    }
  };

  // Handle Refresh Page Loading when update API runs
  if (isPending) {
    return <PageRefresh />;
  }

  return (
    <div
      className={`${globalStyleObj.backgroundLight900Dark300} w-full rounded-sm shadow-light overflow-hidden`}
    >
      {/* Langugage SearchParams Tab Switcher */}
      {isItemDetailsExist && languages.length > 1 && (
        <LanguageTabSwitcher
          languages={languages}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className={`p-3 sm:p-5`}>
        {/* Common Details (name, slug, short_description, description, featured_image, banner_image, button_label, order_number) */}
        {activeSection === "section-1" && (
          <InvestmentCommonFields
            theme={theme}
            watchedDescription={watch("description") || ""}
            control={control}
            errors={errors}
            formData={formData}
            filesList={filesList}
            allFiles={allFiles}
            paginationDetails={paginationDetails}
            handleOnChangeTextEditor={handleOnChangeTextEditor}
            handleOnChangeFile={onChangeImageFiled}
            adminRole={adminRole}
            permissionsList={permissionsList}
            searchValue={searchValue}
            selectedFileType={selectedFileType}
            isItemDetailsExist={isItemDetailsExist}
            isHideInOtherLanguages={isHideInOtherLanguages}
            activeLanguageTab={activeLanguageTab}
          />
        )}

        {/* Features Section (feature_heading, features) */}
        {activeSection === "section-2" && (
          <>
            {/* Features Heading */}
            <CommonTextInputField
              fieldName="feature_heading"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "update-feature-heading"
                  : "feature-heading"
              }
              control={control}
              errors={errors}
              errorsType={errors?.feature_heading}
              placeholderText="Heading"
              labelName="Features Heading"
              inputBoxMaxWidth="xl:max-w-[500px]"
              translateField={isItemDetailsExist}
            />

            {/* Service Features */}
            <RepeatableThreeInputsWithImage
              formData={formData}
              labelText="Features"
              fieldId={isItemDetailsExist ? "update-features" : "features"}
              translateField={isItemDetailsExist}
              repeatableKeyname="features"
              repeatableKeys={["icons", "titles", "descriptions"]}
              addNewField={handleAddRepeatableField}
              removeField={handleRemoveRepeatableField}
              onChangeField={handleOnChangeRepeatableFields}
              filesList={filesList}
              allFiles={allFiles}
              paginationDetails={paginationDetails}
              searchValue={searchValue}
              selectedFileType={selectedFileType}
              adminRole={adminRole}
              permissionsList={permissionsList}
              isDetailsExist={isItemDetailsExist}
              isHideInOtherLanguages={isHideInOtherLanguages}
              extraContainerClasses="mt-5"
              inputBoxMaxWidth="xl:max-w-[500px]"
            />
          </>
        )}

        {/* Key Points Section (key_points_heading, key_points_description) */}
        {activeSection === "section-3" && (
          <>
            {/* Heading */}
            <CommonTextInputField
              fieldName="key_points_heading"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "update-key-points-heading"
                  : "key-points-heading"
              }
              control={control}
              errors={errors}
              errorsType={errors?.name}
              placeholderText="Key Heading"
              labelName="Key Heading"
              inputBoxMaxWidth="xl:max-w-[500px]"
              translateField={isItemDetailsExist}
            />

            {/* Description */}
            <CommonJoditReactTextEditor
              value={formData?.key_points_description || ""}
              labelText="Key Description"
              fieldId={
                isItemDetailsExist
                  ? "update-key-points-description"
                  : "key-points-description"
              }
              fieldName="key_points_description"
              onTextEditorChange={(content) =>
                handleOnChangeTextEditor(content, "key_points_description")
              }
              isItemDetailsExist={isItemDetailsExist}
              extraClass="mt-5"
              inputContainerMaxWidth="xl:max-w-[500px]"
            />
          </>
        )}

        {/* Packages Section (packages_heading, packages_short_description, packages) */}
        {activeSection === "section-4" && (
          <>
            {/* Packages Heading */}
            <CommonTextInputField
              fieldName="packages_heading"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "update-packages-heading"
                  : "packages-heading"
              }
              control={control}
              errors={errors}
              errorsType={errors?.packages_heading}
              placeholderText="Heading"
              labelName="Heading"
              inputBoxMaxWidth="max-w-[500px]"
              translateField={isItemDetailsExist}
            />

            {/* Packages Short Description */}
            <CommonTextAreaField
              fieldName="packages_short_description"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "update-packages-short-description"
                  : "packages-short-description"
              }
              control={control}
              errors={errors}
              errorsType={errors?.packages_short_description}
              labelName="Short Description"
              placeholderText="Short Description"
              extraClass="mt-5"
              inputBoxMaxWidth="max-w-[500px]"
              translateField={isItemDetailsExist}
            />

            {/* Packages Repeater */}
            <RepeatableTextTextTextInput
              formData={formData}
              labelText="Packages"
              fieldId={isItemDetailsExist ? "update-packages" : "packages"}
              translateField={isItemDetailsExist}
              repeatableKeyname="packages"
              repeatableKeys={["sub_titles", "ammounts", "descriptions"]}
              addNewField={handleAddRepeatableField}
              removeField={handleRemoveRepeatableField}
              onChangeField={handleOnChangeRepeatableFields}
              placeHolderText="Text"
              extraContainerClasses="mt-5"
              inputBoxMaxWidth="max-w-[500px]"
            />
          </>
        )}

        {/* Registration Contact Section (registration_contact_heading, registration_contact_sub_heading, registration_contact_email_1, registration_contact_email_2) */}
        {activeSection === "section-5" && (
          <>
            {/* Registration Contact Heading */}
            <CommonTextInputField
              fieldName="registration_contact_heading"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "update-registration-contact-heading"
                  : "registration-contact-heading"
              }
              control={control}
              errors={errors}
              errorsType={errors?.registration_contact_heading}
              placeholderText="Heading"
              labelName="Heading"
              inputBoxMaxWidth="max-w-[500px]"
              translateField={isItemDetailsExist}
            />

            {/* Registration Contact Sub Heading */}
            <CommonTextInputField
              fieldName="registration_contact_sub_heading"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "update-registration-contact-sub-heading"
                  : "registration-contact-sub-heading"
              }
              control={control}
              errors={errors}
              errorsType={errors?.registration_contact_sub_heading}
              placeholderText="Sub Heading"
              labelName="Sub Heading"
              extraClass="mt-5"
              inputBoxMaxWidth="max-w-[500px]"
              translateField={isItemDetailsExist}
            />

            {/* Registration Contact Email 1 */}
            {isHideInOtherLanguages && (
              <CommonTextInputField
                fieldName="registration_contact_email_1"
                fieldType="text"
                fieldId={
                  isItemDetailsExist
                    ? "update-registration-contact-email-1"
                    : "registration-contact-email-1"
                }
                control={control}
                errors={errors}
                errorsType={errors?.registration_contact_email_1}
                placeholderText="Email Option 1"
                labelName="Email Option 1"
                extraClass="mt-5"
                inputBoxMaxWidth="max-w-[500px]"
                extraInformationText="Provide valid email address"
              />
            )}

            {/* Registration Contact Email 2 */}
            {isHideInOtherLanguages && (
              <CommonTextInputField
                fieldName="registration_contact_email_2"
                fieldType="text"
                fieldId={
                  isItemDetailsExist
                    ? "update-registration-contact-email-1"
                    : "registration-contact-email-1"
                }
                control={control}
                errors={errors}
                errorsType={errors?.registration_contact_email_2}
                placeholderText="Email Option 2"
                labelName="Email Option 2"
                extraClass="mt-5"
                inputBoxMaxWidth="max-w-[500px]"
                extraInformationText="Provide valid email address"
              />
            )}
          </>
        )}

        {/* Company Type Section (company_type_heading, company_types) */}
        {activeSection === "section-6" && (
          <>
            {/* Type Heading */}
            <CommonTextInputField
              fieldName="company_type_heading"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "update-company-type-heading"
                  : "company-type-heading"
              }
              control={control}
              errors={errors}
              errorsType={errors?.company_type_heading}
              placeholderText="Heading"
              labelName="Heading"
              inputBoxMaxWidth="max-w-[500px]"
              translateField={isItemDetailsExist}
            />

            <RepeatableTwoTextInputFields
              formData={formData}
              labelText="Types"
              fieldId={
                isItemDetailsExist ? "update-company-types" : "company-types"
              }
              translateField={isItemDetailsExist}
              repeatableKeyname="company_types"
              repeatableKeys={["labels", "values"]}
              addNewField={handleAddRepeatableField}
              removeField={handleRemoveRepeatableField}
              onChangeField={handleOnChangeRepeatableFields}
              firstInputFieldPlaceholder="Label"
              secondInputFieldPlaceholder="Value"
              extraContainerClasses="mt-5"
              inputBoxMaxWidth="xl:max-w-[500px]"
            />
          </>
        )}

        {/* Important Infos Section (important_info_heading, important_infos) */}
        {activeSection === "section-7" && (
          <>
            {/* Important Info Heading */}
            <CommonTextInputField
              fieldName="important_info_heading"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "update-important-info-heading"
                  : "important-info-heading"
              }
              control={control}
              errors={errors}
              errorsType={errors?.important_info_heading}
              placeholderText="Heading"
              labelName="Heading"
              inputBoxMaxWidth="max-w-[500px]"
              translateField={isItemDetailsExist}
            />

            <RepeatableTwoInputsWithTextEditor
              formData={formData}
              labelText="Infos"
              fieldId={
                isItemDetailsExist
                  ? "update-important-infos"
                  : "important-infos"
              }
              translateField={isItemDetailsExist}
              repeatableKeyname="important_infos"
              repeatableKeys={["tags", "descriptions"]}
              addNewField={handleAddRepeatableField}
              removeField={handleRemoveRepeatableField}
              onChangeField={handleOnChangeRepeatableFields}
              firstInputFieldPlaceholder="Tag"
              secondInputFieldPlaceholder="Description"
              extraContainerClasses="mt-5"
              inputBoxMaxWidth="xl:max-w-[500px]"
            />
          </>
        )}

        {/* Meta Infos Section (meta_title, meta_image, meta_keywords, meta_description) */}
        {activeSection === "section-8" && (
          <>
            {/* MetaTitle */}
            <CommonTextInputField
              fieldName="meta_title"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "update-investment-meta-title"
                  : "investment-meta-title"
              }
              control={control}
              errors={errors}
              errorsType={errors?.meta_title}
              placeholderText="Meta Title"
              labelName="Meta Title"
              inputBoxMaxWidth="xl:max-w-[500px]"
            />

            {/* Meta Image */}
            <div className={`mt-5 ${globalStyleObj.commonInputContainerClass}`}>
              <LabelText
                text="Meta Image"
                htmlForId={
                  isItemDetailsExist
                    ? "update-investment-meta-image"
                    : "investment-meta-image"
                }
              />

              <div className="flex flex-col gap-2 w-full xl:max-w-[500px]">
                <FileReuseDialog
                  htmlId={
                    isItemDetailsExist
                      ? "update-investment-meta-image"
                      : "investment-meta-image"
                  }
                  filesList={filesList}
                  allFiles={allFiles}
                  paginationDetails={paginationDetails}
                  searchValue={searchValue}
                  selectedFileType={selectedFileType}
                  onChangeBannerImage={(id) =>
                    handleOnChangeFile("meta_image", id)
                  }
                  selectedBannerFileId={
                    isItemDetailsExist
                      ? (getFileSettingsValue(
                          allFiles,
                          formData?.meta_image || ""
                        )?._id ?? null)
                      : null
                  }
                  selectedBannerFileName={
                    isItemDetailsExist
                      ? (getFileSettingsValue(
                          allFiles,
                          formData?.meta_image || ""
                        )?.fileName ?? null)
                      : null
                  }
                  adminRole={adminRole}
                  permissionsList={permissionsList}
                />
              </div>
            </div>

            {/* Meta Keywords */}
            <CommonTextInputField
              fieldName="meta_keywords"
              fieldType="text"
              fieldId={
                isItemDetailsExist
                  ? "update-investment-meta-keywords"
                  : "investment-meta-keywords"
              }
              control={control}
              errors={errors}
              errorsType={errors?.meta_keywords}
              placeholderText="Meta Keywords"
              labelName="Meta Keywords"
              extraClass="mt-5"
              extraInformationText="Enter comma separated keywords (e.g. keyword1,keyword2,keyword3)"
              inputBoxMaxWidth="xl:max-w-[500px]"
            />

            {/* Meta Description */}
            <CommonTextAreaField
              fieldName="meta_description"
              fieldId={
                isItemDetailsExist
                  ? "update-investment-meta-description"
                  : "investment-meta-description"
              }
              control={control}
              errors={errors}
              errorsType={errors?.meta_description}
              labelName="Meta Description"
              placeholderText="Meta Description"
              extraClass="mt-5"
              inputBoxMaxWidth="xl:max-w-[500px]"
            />
          </>
        )}

        <SubmitButton
          isSubmitting={isSubmitting}
          colorGrade={{ bgColor, hoverBgColor, textColor, hexCode }}
          label={formType === "CREATE_FORM" ? "Add Field" : "Update Field"}
          icon={<MdBusiness />}
        />
      </form>
    </div>
  );
};

export default InvestmentForm;
